/**
 * useMessengerWs — единый WebSocket-композабл провайдеров мессенджеров.
 * Подключается к /ws сервиса messenger-providers и маршрутизирует события по
 * провайдерам: WhatsApp, Zalo и Telegram (DEV-878).
 *
 * Монтируется один раз в DashboardLayout — соединение живёт всю сессию (DEV-2418):
 * входящий звонок на общий номер WhatsApp Business должен дойти на любой
 * странице, а не только в открытой карточке клиента. Раньше сокет поднимали
 * MessengerTabContainer и карточки Telegram в профиле и админке.
 *
 * Прежнее имя — useWhatsAppWs (сохранён ре-экспорт для обратной совместимости).
 */
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { API_CONFIG } from '@/api/config'
import { useMessengerStore } from '@/features/messenger/stores/messenger.store'
import { useZaloStore } from '@/features/messenger/stores/zalo.store'
import { useTelegramStore } from '@/features/messenger/stores/telegram.store'
import { useTelegramSupportStore } from '@/features/messenger/stores/telegramSupport.store'
import { SUPPORT_LOGIN_ID } from '@/features/messenger/stores/telegramSession'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { accountStatusTarget } from '@/features/messenger/utils/accountStatusTarget'
import { useWhatsAppCall } from '@/features/messenger/composables/useWhatsAppCall'
import { useTelegramCall } from '@/features/messenger/composables/useTelegramCall'
import { useWabaCall } from '@/features/messenger/composables/useWabaCall'
import { setActiveWs } from '@/features/messenger/composables/messengerWsBus'
import type { WsServerMessage } from '@/features/messenger/types/messenger.types'
import type { WsProviderStatusPayload } from '@/features/messenger/types/telegram.types'
import { isImpersonating, tokenStorage } from '@/features/auth/tokenStorage'
import { onStaleReturn } from '@/composables/useStaleReturn'

const WS_PATH = '/api/v1/messenger-providers/ws'

// Реконнект без лимита попыток (DEV-2131): 1 с × 2^n с потолком 30 с и джиттером
// ±20 %, чтобы после деплоя сокеты не поднимались строем. Раньше — 10 попыток по 3 с.
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
const RECONNECT_JITTER = 0.2
// DEV-2502. Сколько ждать ответа на проверку живости после возврата во вкладку.
export const PROBE_TIMEOUT_MS = 10_000

/** Задержка перед попыткой номер attempt (с единицы); random подставляют тесты. */
export function reconnectDelayMs(attempt: number, random = Math.random()): number {
  const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.max(0, attempt - 1))
  return Math.round(backoff * (1 + (random * 2 - 1) * RECONNECT_JITTER))
}

/**
 * Адрес сокета из базового адреса API. Относительный базовый адрес (`/` в
 * `.env.local`) раньше давал `ws://api/...` без хоста — локальный стенд жил без
 * живых событий (DEV-2131); теперь он берётся от текущего origin.
 */
export function messengerWsUrl(base: string, origin: string): string {
  const api = new URL(base || '/', origin)
  const protocol = api.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${api.host}${WS_PATH}`
}

/**
 * События логина приходят инициатору в его же соединение, поэтому админ,
 * подключающий общий аккаунт, получает их вперемешку со своими. Разбираем по
 * login_id: 'support' — общий аккаунт, всё остальное (в том числе событие без
 * поля, от старого бэкенда) — личный (DEV-1712).
 */
const telegramLoginTarget = (loginId?: string) =>
  loginId === SUPPORT_LOGIN_ID ? useTelegramSupportStore() : useTelegramStore()

/**
 * Статус аккаунта, в отличие от логина, приходит и admin/hos (DEV-2143): чужой
 * login_id не должен перекрашивать личную карточку — его пропускаем (DEV-2144).
 */
/**
 * DEV-2227: снимок PROVIDER_STATUS приходит при каждом подключении — статусы Telegram
 * свежие и после разрыва, отзыв в разрыв не теряется. WhatsApp живёт на LIST_DEVICES.
 */
function applyTelegramSnapshot(snapshot: WsProviderStatusPayload | null) {
  if (snapshot?.telegram) useTelegramStore().handleAccountSnapshot(snapshot.telegram)
  if (snapshot?.telegram_support) {
    useTelegramSupportStore().applyAccountSnapshot(snapshot.telegram_support)
  }
}

const telegramAccountStatusTarget = (loginId?: string) => {
  switch (accountStatusTarget(loginId, useAuthStore().user?.id)) {
    case 'support':
      return useTelegramSupportStore()
    case 'own':
      return useTelegramStore()
    default:
      return null
  }
}

export function useMessengerWs() {
  const store = useMessengerStore()
  const isConnected = ref(false)
  const reconnectAttempts = ref(0)

  let ws: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  // Было ли соединение ранее — чтобы отличить первый connect от переподключения
  // (после разрыва досинхронизируем открытый чат: события в разрыв теряются). DEV-988.
  let hadConnection = false
  // После unmount переподключаться нельзя (DEV-2131).
  let stopped = false
  // Проверка живости после возврата во вкладку (DEV-2502).
  let probeTimer: ReturnType<typeof setTimeout> | null = null

  function getWsUrl(): string {
    const token = tokenStorage.getAccessToken() || ''
    const url = messengerWsUrl(API_CONFIG.BASE_URL, window.location.origin)
    return `${url}?token=${encodeURIComponent(token)}`
  }

  function routeMessage(msg: WsServerMessage) {
    switch (msg.code) {
      case 'PROVIDER_STATUS':
        // Первым: сбой разбора Telegram не должен оставить вкладку WhatsApp на скелете.
        store.applyProviderStatus(msg.result)
        applyTelegramSnapshot(msg.result)
        break

      // ---- WhatsApp ----
      case 'LIST_DEVICES':
        store.updateDevicesFromWs(msg.result)
        break
      case 'LOGIN_SUCCESS':
      case 'DEVICE_CONNECTED':
      case 'DEVICE_REMOVED':
        // После логина/подключения/удаления устройства — запрашиваем актуальный
        // список (DEV-276: DEVICE_CONNECTED закрывает QR без перезагрузки).
        ws?.send(JSON.stringify({ code: 'FETCH_DEVICES', message: '', result: null }))
        break
      case 'NEW_MESSAGE':
        store.addMessageFromWs(msg.result)
        break
      case 'MESSAGE_STATUS':
        store.updateMessageStatus(msg.result)
        break
      case 'MESSAGE_REACTION':
        store.updateMessageReaction(msg.result)
        break
      case 'MESSAGE_MEDIA_UPDATE':
        store.updateMessageMedia(msg.result)
        break
      case 'CLIENT_FILE_ADDED':
        store.notifyClientFileAdded()
        break
      case 'WHATSAPP_CALL_STATE': {
        const { handleCallStateEvent } = useWhatsAppCall()
        handleCallStateEvent(msg.result)
        break
      }

      // ---- Zalo ----
      case 'NEW_ZALO_MESSAGE':
        useZaloStore().addMessageFromWs(msg.result)
        break
      case 'ZALO_MESSAGE_SENT':
        useZaloStore().handleMessageSent(msg.result)
        break

      // ---- Telegram (DEV-878) ----
      case 'TELEGRAM_QR_UPDATE':
        telegramLoginTarget(msg.result?.login_id).handleQrUpdate(msg.result)
        break
      case 'TELEGRAM_LOGIN_STATUS':
        telegramLoginTarget(msg.result?.login_id).handleLoginStatus(msg.result)
        break
      case 'TELEGRAM_ACCOUNT_STATUS':
        telegramAccountStatusTarget(msg.result?.login_id)?.handleAccountStatus(msg.result)
        break
      case 'NEW_TELEGRAM_MESSAGE':
        useTelegramStore().addMessageFromWs(msg.result)
        break
      case 'TELEGRAM_MESSAGE_SENT':
        useTelegramStore().handleMessageSent(msg.result)
        break
      case 'TELEGRAM_MESSAGE_STATUS':
        useTelegramStore().updateMessageStatus(msg.result)
        break
      case 'TELEGRAM_MESSAGE_MEDIA_UPDATE':
        useTelegramStore().updateMessageMedia(msg.result)
        break
      case 'TELEGRAM_MESSAGE_REACTION':
        useTelegramStore().updateMessageReaction(msg.result)
        break
      case 'TELEGRAM_MESSAGE_EDITED':
        useTelegramStore().handleEdited(msg.result)
        break
      case 'TELEGRAM_MESSAGE_DELETED':
        useTelegramStore().handleDeleted(msg.result)
        break
      case 'TELEGRAM_TYPING':
        useTelegramStore().setTyping(msg.result)
        break

      // ---- Telegram-звонки (DEV-924) ----
      case 'TELEGRAM_CALL_SDP_OFFER':
        void useTelegramCall().handleSdpOffer(msg.result)
        break
      case 'TELEGRAM_CALL_ICE':
        void useTelegramCall().handleRemoteIce(msg.result)
        break
      case 'TELEGRAM_CALL_STATE':
        useTelegramCall().handleState(msg.result)
        break
      case 'TELEGRAM_CALL_ENDED':
        useTelegramCall().handleEnded(msg.result)
        break
      case 'TELEGRAM_CALL_INCOMING':
        useTelegramCall().handleIncoming(msg.result)
        break
      case 'TELEGRAM_CALL_ANALYSIS_COMPLETED':
        useTelegramCall().handleAnalysisCompleted(msg.result)
        break

      // ---- Звонки на общий номер WhatsApp Business (DEV-2418) ----
      case 'WABA_CALL_INCOMING':
        useWabaCall().handleIncoming(msg.result)
        break
      case 'WABA_CALL_SDP_OFFER':
        void useWabaCall().handleSdpOffer(msg.result)
        break
      case 'WABA_CALL_ICE':
        void useWabaCall().handleRemoteIce(msg.result)
        break
      case 'WABA_CALL_STATE':
        useWabaCall().handleState(msg.result)
        break
      case 'WABA_CALL_ENDED':
        useWabaCall().handleEnded(msg.result)
        break
      case 'WABA_CALL_ANALYSIS_COMPLETED':
        useWabaCall().handleAnalysisCompleted(msg.result)
        break
    }
  }

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function isLive(socket: WebSocket | null): socket is WebSocket {
    return (
      !!socket &&
      (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)
    )
  }

  function connect() {
    stopped = false
    if (isLive(ws)) return
    clearReconnectTimer()

    // Вкладка просмотра (DEV-2399): сервер сокет с токеном просмотра не примет,
    // а переподключение здесь бесконечное. История чатов грузится запросами.
    if (isImpersonating()) return

    const token = tokenStorage.getAccessToken()
    if (!token) {
      console.warn('[MessengerWs] No access token, skipping WS connection')
      return
    }

    const socket = new WebSocket(getWsUrl())
    ws = socket

    socket.onopen = () => {
      if (ws !== socket) return
      isConnected.value = true
      reconnectAttempts.value = 0
      setActiveWs(socket)
      console.debug('[MessengerWs] WebSocket connected')
      socket.send(JSON.stringify({ code: 'FETCH_DEVICES', message: '', result: null }))
      // Переподключение → досинхронизируем открытые чаты: статусы прочтения и
      // сообщения, потерянные за время разрыва, подтягиваются из БД (DEV-988). До
      // DEV-2502 — только Telegram: WhatsApp и Zalo без перезагрузки не догоняли.
      // И после возврата во вкладку, хотя чаты только что перечитаны по HTTP: сообщение
      // между тем снимком и подпиской этого сокета иначе не пришло бы ни одним путём.
      if (hadConnection) resyncChats()
      hadConnection = true
    }

    socket.onmessage = (event) => {
      if (ws !== socket) return
      // Любое сообщение — сокет жив.
      clearProbe()
      try {
        routeMessage(JSON.parse(event.data) as WsServerMessage)
      } catch (e) {
        console.error('[MessengerWs] Failed to parse WS message:', e)
      }
    }

    socket.onclose = () => {
      // Закрылся уже заменённый сокет — текущее соединение не трогаем.
      if (ws !== socket) return
      clearProbe()
      isConnected.value = false
      ws = null
      setActiveWs(null)
      console.debug('[MessengerWs] WebSocket disconnected')
      if (!stopped) scheduleReconnect()
    }

    socket.onerror = (err) => {
      console.error('[MessengerWs] WebSocket error:', err)
    }
  }

  function scheduleReconnect() {
    clearReconnectTimer()
    reconnectAttempts.value++
    const delay = reconnectDelayMs(reconnectAttempts.value)
    console.debug(`[MessengerWs] Reconnecting in ${delay} ms (attempt ${reconnectAttempts.value})`)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      connect()
    }, delay)
  }

  // Сеть вернулась или вкладка снова на экране — не ждём таймер (DEV-2131): после
  // сна ноутбука первая попытка иначе уходила бы через полминуты.
  function reconnectNow() {
    if (stopped || isLive(ws)) return
    connect()
  }
  const onOnline = () => reconnectNow()
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return
    if (isLive(ws)) probe()
    else reconnectNow()
  }

  function resyncChats() {
    void useTelegramStore().reloadCurrentChat()
    void store.reloadCurrentChat()
    void useZaloStore().reloadCurrentChat()
  }

  function clearProbe() {
    if (probeTimer) {
      clearTimeout(probeTimer)
      probeTimer = null
    }
  }

  /**
   * DEV-2502. Сон телефона или ноутбука оставляет «зомби»: readyState OPEN, а onclose
   * не приходит — события молча теряются. На FETCH_DEVICES сервер отвечает
   * LIST_DEVICES (своего PING у него нет): нет ответа — пересоздаём.
   */
  function probe() {
    if (!ws || ws.readyState !== WebSocket.OPEN || probeTimer) return
    const socket = ws
    socket.send(JSON.stringify({ code: 'FETCH_DEVICES', message: '', result: null }))
    probeTimer = setTimeout(() => {
      probeTimer = null
      if (ws === socket) recreate()
    }, PROBE_TIMEOUT_MS)
  }

  /** Новый сокет вместо текущего: onclose старого не ждём — у зомби его не будет. */
  function recreate() {
    if (stopped) return
    clearProbe()
    if (ws) {
      const socket = ws
      ws = null
      setActiveWs(null)
      isConnected.value = false
      socket.close()
    }
    connect()
  }

  // Вкладка была скрыта дольше 5 минут: открытые чаты — сразу по HTTP (сокет может и не
  // подняться: деплой, прокси режет WS), и новый сокет вместо «зомби». Во время звонка
  // сокет не трогаем (по нему идут ICE и сброс), во вкладке просмотра его нет.
  onStaleReturn(() => {
    if (stopped) return
    resyncChats()
    const inCall =
      useWabaCall().isActive.value ||
      useTelegramCall().isActive.value ||
      useWhatsAppCall().isActive.value
    if (!inCall && !isImpersonating()) recreate()
  })

  function disconnect() {
    stopped = true
    clearReconnectTimer()
    clearProbe()
    if (ws) {
      const socket = ws
      ws = null
      socket.close()
    }
    setActiveWs(null)
    isConnected.value = false
  }

  onMounted(() => {
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)
    connect()
  })

  onBeforeUnmount(() => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    disconnect()
  })

  return {
    isConnected,
    connect,
    disconnect,
  }
}
